import { useState } from "react";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { PERSONALITIES, VOICES, SKINS, HAIRS, STYLES, speak } from "@/lib/buddy";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";

const Chips = ({ label, options, value, onChange, testId }) => (
  <div>
    <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map(([v, l, hint]) => (
        <button type="button" key={v} onClick={() => onChange(v)} data-testid={`${testId}-${v.replace(/\s+/g, "-")}`} title={hint}
          className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${value === v ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white/70 border-[#E6DEC3] hover:border-[#2C4A3E]"}`}>
          {l || v}
        </button>
      ))}
    </div>
  </div>
);

const opts = (arr) => arr.map((v) => [v, v]);

export const BuddyCreator = ({ onDone }) => {
  const { buddy, setBuddy, profile } = useProfile();
  const [f, setF] = useState(() => buddy ? { name: buddy.name, personality: buddy.personality, voice: buddy.voice, look: buddy.look }
    : { name: "", personality: "encouraging", voice: "nova", look: { age: "peer", gender: "any", skin: "warm brown", hair: "short curly black", style: "a green hoodie", extra: "" } });
  const [busy, setBusy] = useState("");
  const look = (k) => (v) => setF((s) => ({ ...s, look: { ...s.look, [k]: v } }));

  const save = async (portrait) => {
    if (!f.name.trim()) return toast.error("Give your buddy a name first");
    setBusy(portrait ? "portrait" : "save");
    try {
      let r = await http.put("/buddy", f);
      if (portrait) r = await http.post("/buddy/portrait");
      setBuddy(r.data.buddy);
      toast.success(portrait ? `Say hi to ${f.name}!` : "Buddy saved");
      if (portrait) onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10" data-testid="buddy-creator">
      <div className="lg:col-span-4">
        <div className="rounded-[2rem] bg-[#F7F3EB] border border-[#E6DEC3] p-6 grid place-items-center aspect-square relative overflow-hidden">
          {busy === "portrait" ? <div className="text-center text-[#2C4A3E]"><Loader2 className="w-8 h-8 animate-spin mx-auto" /><p className="mt-3 font-display">Bringing {f.name} to life…</p></div>
            : <BuddyAvatar buddy={buddy} className="w-full h-full !rounded-[1.5rem] text-6xl" testId="creator-portrait" />}
        </div>
        <p className="text-xs text-stone-500 mt-3">Your buddy is an AI companion made just for you, {profile.name}. Portraits are AI-generated.</p>
      </div>
      <div className="lg:col-span-8 space-y-6">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={30} placeholder="Name your buddy (e.g. Kai, Luna, Zara)" data-testid="buddy-name-input"
          className="w-full px-5 py-3 rounded-2xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] font-display text-xl" />
        <Chips label="Personality" options={PERSONALITIES} value={f.personality} onChange={(v) => setF({ ...f, personality: v })} testId="buddy-personality" />
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1"><Chips label="Voice" options={VOICES} value={f.voice} onChange={(v) => setF({ ...f, voice: v })} testId="buddy-voice" /></div>
          <button type="button" data-testid="buddy-voice-preview-btn" onClick={() => speak(`Hi ${profile.name}, I'm ${f.name || "your buddy"}. Let's learn together.`, f.voice).catch(() => toast.error("Voice unavailable"))}
            className="text-sm text-[#C87D53] underline underline-offset-4">Preview voice</button>
        </div>
        <Chips label="Age" options={[["peer", "About my age"], ["older", "Older mentor"]]} value={f.look.age} onChange={look("age")} testId="buddy-age" />
        <Chips label="Buddy is a" options={[["any", "Surprise me"], ["girl", "Girl"], ["boy", "Boy"]]} value={f.look.gender} onChange={look("gender")} testId="buddy-gender" />
        <Chips label="Skin tone" options={opts(SKINS)} value={f.look.skin} onChange={look("skin")} testId="buddy-skin" />
        <Chips label="Hair" options={opts(HAIRS)} value={f.look.hair} onChange={look("hair")} testId="buddy-hair" />
        <Chips label="Style" options={opts(STYLES)} value={f.look.style} onChange={look("style")} testId="buddy-style" />
        <input value={f.look.extra} onChange={(e) => look("extra")(e.target.value)} maxLength={160} placeholder="Anything else? (glasses, freckles, headphones…)" data-testid="buddy-extra-input"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm" />
        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={() => save(true)} disabled={!!busy} data-testid="buddy-generate-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B] disabled:opacity-60 transition-colors">
            {busy === "portrait" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {buddy?.avatar_url ? "Save & redraw portrait" : "Create my buddy"}
          </button>
          {buddy && <button onClick={() => save(false)} disabled={!!busy} data-testid="buddy-save-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E6DEC3] text-[#2C4A3E] hover:bg-[#F1EADB] disabled:opacity-60 transition-colors">
            <Sparkles className="w-4 h-4" /> Save without redrawing</button>}
        </div>
      </div>
    </div>
  );
};
